import { memo, useCallback, useMemo } from 'react';

import {
  ChakraFlex,
  ChakraGrid,
  ChakraGridItem,
  ChakraSimpleGrid,
  CheckboxV2,
  FAIconName,
  GooglePlacesAutoComplete,
  InputGroupV2,
  RadioV2,
  TextInputV2,
  UniversalSelect,
  convertDashedUSPhoneNumberToInternationalFormat,
} from '@equip.health/ui';
import { validate as validateEmail } from 'email-validator';

import { ReferralInfoValue } from '~/components/common/inquiry/drawer/types';
import { VITE_GOOGLE_PLACES_API_KEY } from '~/lib/constants/env';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import {
  buildUniversalSelectSearchTerm,
  dropdownMenuOptionsMapper,
  getReferrerTypesFromEnumeration,
  getSelectedUniversalSelectOption,
} from '~/lib/util/inquiry.util';
import util from '~/lib/util/util';

import InquiryDrawerLayout from './InquiryDrawerLayout';
import {
  useGetProviderStatesEnumQuery,
  useGetRelationsToPatientEnumQuery,
} from '~/store/services/enumerations';
import { RelationToPatientCategory } from '~/lib/constants/enumeration.constants';

const TOP_LEVEL_ID = 'referral-info';

interface ReferralInfoProps {
  referralInfoValue: ReferralInfoValue;
  showRequiredFieldErrors: boolean;
  shouldShowReferrerTypeField: boolean;
  updateReferralInfo: (info: Partial<ReferralInfoValue>) => void;
}

const ReferralInfo = memo(
  ({
    referralInfoValue,
    shouldShowReferrerTypeField,
    showRequiredFieldErrors,
    updateReferralInfo,
  }: ReferralInfoProps) => {
    const { data: relationsToPatientInContact } =
      useGetRelationsToPatientEnumQuery();
    const nonFamilyRelationsToPatientInPatientContact = useMemo<
      RelationToPatientEnumeration[]
    >(
      () =>
        (relationsToPatientInContact || [])
          .filter((x) => x.category === RelationToPatientCategory.NON_FAMILY)
          .sort((a, b) => a.label.localeCompare(b.label)),
      [relationsToPatientInContact],
    );

    const { data: states } = useGetProviderStatesEnumQuery();

    const {
      addressLine1,
      addressLine2,
      name,
      city,
      contactExternalId,
      email,
      faxNumber,
      firstName,
      isOngoingProvider,
      isPrimaryContact,
      lastName,
      latitude,
      longitude,
      officeExtension,
      officePhone,
      prefix,
      referrerType,
      relationToPatient,
      state,
      zipCode,
    } = referralInfoValue;
    const anyFieldFilled =
      contactExternalId?.trim()?.length > 0 ||
      firstName?.trim()?.length > 0 ||
      lastName?.trim()?.length > 0 ||
      relationToPatient?.length > 0 ||
      addressLine1?.trim().length > 0 ||
      email?.trim().length > 0 ||
      addressLine2?.length > 0 ||
      city?.trim().length > 0 ||
      zipCode?.length > 0 ||
      state?.trim().length > 0 ||
      referrerType?.length > 0 ||
      prefix?.trim()?.length > 0 ||
      officePhone?.trim()?.length > 0 ||
      faxNumber?.trim()?.length > 0 ||
      isPrimaryContact === true;

    const isEmailValid = useMemo<boolean>(() => validateEmail(email), [email]);
    const isPhoneValid = useMemo<boolean>(
      () =>
        util.validatePhoneNumber(
          convertDashedUSPhoneNumberToInternationalFormat(officePhone ?? ''),
        ),
      [officePhone],
    );

    const isFaxValid = useMemo<boolean>(
      () =>
        util.validatePhoneNumber(
          convertDashedUSPhoneNumberToInternationalFormat(faxNumber ?? ''),
        ),
      [faxNumber],
    );

    const isEmailRequired = !!isPrimaryContact && !isPhoneValid;
    const isPhoneRequired = !!isPrimaryContact && !isEmailValid;
    const isFaxRequired = !!isPrimaryContact && !isFaxValid;
    const showIsInvalidEmail =
      (showRequiredFieldErrors && isEmailRequired && !isEmailValid) ||
      (email && !isEmailValid);
    const showIsInvalidPhone =
      (showRequiredFieldErrors && isPhoneRequired && !isPhoneValid) ||
      (officePhone && !isPhoneValid);
    const showIsInvalidFax =
      (showRequiredFieldErrors && isFaxRequired && !isFaxValid) ||
      (faxNumber && !isFaxValid);
    const invalidFirstName =
      showRequiredFieldErrors && anyFieldFilled && !firstName?.trim();
    const invalidLastName =
      showRequiredFieldErrors && anyFieldFilled && !lastName?.trim();
    const invalidReferrerType =
      showRequiredFieldErrors && anyFieldFilled && !referrerType;
    const invalidRelationToPatient =
      showRequiredFieldErrors && anyFieldFilled && !relationToPatient;

    const isMissingRequired =
      showIsInvalidEmail ||
      showIsInvalidPhone ||
      showIsInvalidFax ||
      invalidFirstName ||
      invalidLastName ||
      invalidReferrerType ||
      invalidRelationToPatient;

    const referrerTypeOptions = useMemo<UniversalSelectOption[]>(() => {
      return dropdownMenuOptionsMapper(
        getReferrerTypesFromEnumeration(
          nonFamilyRelationsToPatientInPatientContact,
        ),
      );
    }, [nonFamilyRelationsToPatientInPatientContact]);

    const relationToPatientOptions = useMemo<UniversalSelectOption[]>(() => {
      return (nonFamilyRelationsToPatientInPatientContact ?? [])
        .filter((item) => item?.subcategory === referrerType)
        .map(({ label }) => ({
          label,
          searchData: buildUniversalSelectSearchTerm(label),
          value: label,
        }));
    }, [referrerType, nonFamilyRelationsToPatientInPatientContact]);

    const updateStringValue = useCallback(
      (key: keyof ReferralInfoValue) =>
        (val: string): void => {
          updateReferralInfo({ [key]: val });
        },
      [],
    );

    return (
      <InquiryDrawerLayout
        header="Referral information"
        id={TOP_LEVEL_ID}
        isMissingFields={isMissingRequired}
      >
        <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
          <ChakraGrid columnGap="12px" templateColumns="repeat(3, 1fr)">
            <ChakraGridItem colSpan={1}>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__prefix`}
                isRequired={false}
                label="Prefix"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__prefix`}
                  onChange={updateStringValue('prefix')}
                  value={prefix}
                />
              </InputGroupV2>
            </ChakraGridItem>
            <ChakraGridItem colSpan={2}>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__first-name`}
                isRequired={anyFieldFilled}
                label="First name"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__first-name`}
                  isInvalid={invalidFirstName}
                  onChange={updateStringValue('firstName')}
                  value={firstName}
                />
              </InputGroupV2>
            </ChakraGridItem>
          </ChakraGrid>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__last-name`}
            isRequired={anyFieldFilled}
            label="Last name"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__last-name`}
              isInvalid={invalidLastName}
              onChange={updateStringValue('lastName')}
              value={lastName}
            />
          </InputGroupV2>
          <ChakraGrid columnGap="12px" templateColumns="repeat(3, 1fr)">
            <ChakraGridItem colSpan={2}>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__office-number`}
                isRequired={isPhoneRequired}
                label="Office number"
              >
                <TextInputV2
                  id={`${TOP_LEVEL_ID}__office-number`}
                  isInvalid={showIsInvalidPhone}
                  leftIcon={FAIconName.mobileRegular}
                  onChange={(val: string) =>
                    updateReferralInfo({
                      isInvalidOfficePhone: !util.validatePhoneNumber(
                        convertDashedUSPhoneNumberToInternationalFormat(
                          val ?? '',
                        ),
                      ),
                      officePhone: val,
                    })
                  }
                  type={TextInputV2Type.PHONE}
                  value={officePhone}
                />
              </InputGroupV2>
            </ChakraGridItem>
            <ChakraGridItem colSpan={1}>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__ext`}
                isRequired={false}
                label="Ext"
              >
                <TextInputV2
                  characterLimit={6}
                  id={`${TOP_LEVEL_ID}__ext`}
                  inputMode="numeric"
                  onChange={updateStringValue('officeExtension')}
                  type={TextInputV2Type.NUMBER}
                  value={officeExtension}
                />
              </InputGroupV2>
            </ChakraGridItem>
          </ChakraGrid>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__fax-number`}
            isRequired={false}
            label="Fax number"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__fax-number`}
              isInvalid={showIsInvalidFax}
              leftIcon={FAIconName.faxRegular}
              onChange={(val: string) =>
                updateReferralInfo({
                  faxNumber: val,
                })
              }
              type={TextInputV2Type.PHONE}
              value={faxNumber}
            />
          </InputGroupV2>
          {shouldShowReferrerTypeField && (
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__referrer-type`}
              isRequired={anyFieldFilled}
              label="Referrer type"
            >
              <UniversalSelect
                id={`${TOP_LEVEL_ID}__referrer-type`}
                isInvalid={invalidReferrerType}
                isSearchable
                isSingleSelect
                onChange={(selected: UniversalSelectOption) =>
                  updateReferralInfo({
                    referrerType: selected?.value,
                    relationToPatient: null,
                  })
                }
                options={referrerTypeOptions}
                value={getSelectedUniversalSelectOption(
                  referrerType,
                  referrerTypeOptions,
                )}
              />
            </InputGroupV2>
          )}
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__relation-to-patient`}
            isRequired={anyFieldFilled}
            label="Relationship to patient"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__relation-to-patient`}
              isDisabled={!referrerType}
              isInvalid={invalidRelationToPatient}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateReferralInfo({
                  relationToPatient: selected?.value,
                })
              }
              options={relationToPatientOptions}
              value={getSelectedUniversalSelectOption(
                relationToPatient,
                relationToPatientOptions,
              )}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__email-address`}
            isRequired={isEmailRequired}
            label="Email address"
          >
            <TextInputV2
              id={`${TOP_LEVEL_ID}__email-address`}
              isInvalid={showIsInvalidEmail}
              leftIcon={FAIconName.envelopeRegular}
              onChange={(val: string) =>
                updateReferralInfo({
                  email: val,
                  isInvalidEmail: !validateEmail(val),
                })
              }
              type={TextInputV2Type.EMAIL}
              value={email}
            />
          </InputGroupV2>
          <ChakraFlex alignItems="center">
            <CheckboxV2
              id={`${TOP_LEVEL_ID}__ongoing-provider`}
              isChecked={isOngoingProvider}
              label="Set as ongoing provider"
              onChange={(val: boolean) =>
                updateReferralInfo({
                  isOngoingProvider: val,
                })
              }
            />
          </ChakraFlex>
        </ChakraSimpleGrid>
        <ChakraSimpleGrid columns={1} spacing="24px" width="100%">
          <GooglePlacesAutoComplete
            addressData={{
              addressLine1,
              addressLine2,
              city,
              latitude,
              longitude,
              name,
              state,
              zipCode,
            }}
            apiKey={VITE_GOOGLE_PLACES_API_KEY}
            hideNA
            id={TOP_LEVEL_ID}
            label="Address"
            requiredFields={[]}
            saveAddressData={(data: ReferrerAffiliation) => {
              updateReferralInfo(data);
            }}
            statesMenuOptions={(states ?? []).map((state) => ({
              node: state.label,
              value: state.externalId,
            }))}
            updateAddress={(value: string, field: string) => {
              updateReferralInfo({
                [field]: value,
              });
            }}
          />
        </ChakraSimpleGrid>
        <RadioV2
          id={`${TOP_LEVEL_ID}__referrer-contact`}
          isChecked={isPrimaryContact}
          label="Referrer is primary contact"
          onChange={(val: boolean) =>
            updateReferralInfo({
              isPrimaryContact: val,
            })
          }
        />
      </InquiryDrawerLayout>
    );
  },
);

export default ReferralInfo;
